import { BaseCabinet } from '../base/BaseCabinet';
import { TypeCabinet } from '../../../constants/enum';
import { KitchenModule } from '../modules/KitchenModule';

export class KitchenCabinet extends BaseCabinet {
  constructor(sceneManager) {
    super(TypeCabinet.KITCHEN, sceneManager);
  }

  /**
   * Gán module đang chỉnh sửa dựa vào group.uuid
   */
  setActiveEditorModuleByUuid(uuid) {
    const module = this.getModuleByGroupUuid(uuid);
    if (module) {
      this.activeEditorModule = module;
      return true;
    }
    return false;
  }

  setCloneModule(module) {
    if (module) {
      this.cloneModule = module;
    }
  }

  getCloneModule() {
    return this.cloneModule || null;
  }

  getActiveEditorModule() {
    return this.activeEditorModule || null;
  }

  getActiveMeshPoints(module) {
    if (module) {
      return module?.getMeshPoints?.() || [];
    } else {
      return this.cloneModule?.getMeshPoints?.() || [];
    }
  }

  changeBevelebCabinet(beveled) {
    super.changeBevelebCabinet(beveled);
  }

  /**
   * Implementation của abstract method từ BaseCabinet
   * Tạo KitchenModule instance
   */
  createModule(moduleData, moduleType, constraints) {
    return new KitchenModule(
      moduleData,
      moduleType,
      this.sceneManager,
      this,
      constraints
    );
  }

  /**
   * Implementation của abstract method từ BaseCabinet
   * Deserialize module từ saved data
   */
  async deserializeModule(moduleData) {
    const module = KitchenModule.fromData(moduleData, this.sceneManager, this);
    await module.load();
    return module;
  }

  /**
   * Override updateModule để implement kitchen-specific logic
   */
  updateModule(module) {
    super.updateModule(module);

    // Recalculate position based on kitchen constraints
    const newTransform = module.calculateKitchenPosition();
    module.setPosition(newTransform.position);
    module.setRotation(newTransform.rotation);
  }

  /**
   * Override repositionModulesByType cho kitchen-specific positioning
   */
  repositionModulesByType(moduleType, modules) {
    // Sort modules theo vị trí hiện tại
    modules.sort((a, b) => {
      // Sort theo z-coordinate (dọc theo tường)
      return a.position.z - b.position.z;
    });

    // Reset positions của tất cả modules cùng type
    modules.forEach((module, index) => {
      // Clear existing position
      module.customData.isRepositioning = true;

      // Recalculate position
      const newTransform = module.calculateKitchenPosition();
      module.setPosition(newTransform.position);
      module.setRotation(newTransform.rotation);

      module.customData.isRepositioning = false;
    });
  }

  /**
   * Event hook khi module được thêm
   */
  onModuleAdded(module) {
    super.onModuleAdded(module);

    // Kitchen-specific logic khi thêm module

    // Có thể trigger update UI, recalculate lighting, etc.
  }

  /**
   * Event hook khi module được xóa
   */
  onModuleRemoved(module) {
    super.onModuleRemoved(module);

    // // Reposition các modules cùng type để lấp đầy khoảng trống
    // const sameTypeModules = this.getModulesByType(module.cabinetType);
    // if (sameTypeModules.length > 0) {
    //   this.repositionModulesByType(module.cabinetType, sameTypeModules);
    // }
  }

  // Kitchen-specific helper methods

  /**
   * Lấy tổng chiều rộng của modules theo type
   */
  getTotalWidthByType(moduleType) {
    const modules = this.getModulesByType(moduleType);
    return modules.reduce((total, module) => total + module.actualSize.x, 0);
  }

  /**
   * Lấy available space trên tường cho type
   */
  getAvailableSpaceForType(moduleType) {
    const floorSize = this.sceneManager.getFloorSize();
    const usedWidth = this.getTotalWidthByType(moduleType);
    return floorSize.x - usedWidth; // Simplified calculation
  }

  /**
   * Kiểm tra có thể thêm module không
   */
  canAddModule(moduleData, moduleType) {
    const availableSpace = this.getAvailableSpaceForType(moduleType);
    const moduleWidth = moduleData.width || 600; // Default width

    return availableSpace >= moduleWidth;
  }

  /**
   * Lấy thống kê kitchen
   */
  getKitchenStats() {
    return {
      ...this.getDebugInfo(),
      totalLowerWidth: this.getTotalWidthByType('lower'),
      totalUpperWidth: this.getTotalWidthByType('upper'),
      totalCeilingWidth: this.getTotalWidthByType('ceiling'),
      availableSpaces: {
        lower: this.getAvailableSpaceForType('lower'),
        upper: this.getAvailableSpaceForType('upper'),
        ceiling: this.getAvailableSpaceForType('ceiling'),
      },
    };
  }

  /**
   * Open tất cả doors
   */
  openAllDoors() {
    this.modules.forEach(module => {
      if (module.openDoor) {
        module.openDoor();
      }
    });
  }

  /**
   * Close tất cả doors
   */
  closeAllDoors() {
    this.modules.forEach(module => {
      if (module.closeDoor) {
        module.closeDoor();
      }
    });
  }

  /**
   * Thay đổi material cho tất cả modules
   */
  changeAllMaterials(material) {
    this.modules.forEach(module => {
      if (module.changeMaterial) {
        module.changeMaterial(material);
      }
    });
  }

  /**
   * Thay đổi màu cho tất cả modules
   */
  changeAllColors(color) {
    this.modules.forEach(module => {
      if (module.changeColor) {
        module.changeColor(color);
      }
    });
  }

  /**
   * Abstract method - phải được override bởi subclass
   * Trả về constraints cho loại tủ này
   */
  getConstraints() {}
}
